import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Loader2, Trash2, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import ConfirmDialog from '../ui/ConfirmDialog';
import AutoSaveIndicator from '../ui/AutoSaveIndicator';
import { useFormAutoSave } from '../../hooks/useFormAutoSave';

// Un juge (mode Course, `event_staff`) dans un panneau latéral (2026-09-27),
// même grammaire que les équipes et les bénévoles.
//
// L'email est l'IDENTITÉ du juge : le serveur fait un upsert par email et
// envoie l'invitation à la création. En édition il est donc en lecture seule —
// « changer » l'email créerait un second juge et partirait un second email.
//
// Sauvegarde automatique (règle du 22/09) : prénom au blur, rôle au choix.
// L'AJOUT garde son bouton : c'est lui qui fait partir l'invitation, un email
// chez quelqu'un d'autre.

export const STAFF_ROLES = ['judge', 'head_judge', 'race_director'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];

export interface StaffMember {
  id: string;
  email: string;
  first_name: string | null;
  role: StaffRole;
  has_account: boolean;
  user_name: string | null;
}

export interface StaffFormValues {
  first_name: string;
  email: string;
  role: StaffRole;
}

export interface StaffPatch {
  first_name?: string;
  role?: StaffRole;
}

function toValues(s: StaffMember | null): StaffFormValues {
  return { first_name: s?.first_name ?? '', email: s?.email ?? '', role: s?.role ?? 'judge' };
}

const toPatch = (fields: (keyof StaffFormValues)[], v: StaffFormValues): StaffPatch => {
  const p: StaffPatch = {};
  if (fields.includes('first_name')) p.first_name = v.first_name.trim();
  if (fields.includes('role')) p.role = v.role;
  return p;
};

interface Props {
  open: boolean;
  /** null = ajout. */
  member: StaffMember | null;
  saving: boolean;
  serverError: string;
  /** Ajout seulement (l'invitation part). */
  onSubmit: (values: StaffFormValues) => void;
  /** Édition : écriture partielle, immédiate. Lève sur refus. */
  onPatch: (patch: StaffPatch) => Promise<void>;
  onRemove: () => void;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export function StaffSheet({ open, member, saving, serverError, onSubmit, onPatch, onRemove, onClose, onDirtyChange, onPrev, onNext }: Props) {
  const { t } = useTranslation();
  const editing = !!member;

  const schema = useMemo(() => z.object({
    first_name: z.string(),
    email: z.string().trim().email(t('teams.form.emailInvalid')),
    role: z.enum(STAFF_ROLES),
  }), [t]);

  const form = useForm<StaffFormValues>({ resolver: zodResolver(schema), defaultValues: toValues(member), mode: 'onTouched' });
  const autoSave = useFormAutoSave<StaffFormValues, StaffPatch>(form, {
    enabled: editing,
    save: onPatch,
    toPatch,
    onError: (err: any) => toast.error(err?.response?.data?.detail || t('eventStaff.addError')),
  });
  const { saveFields } = autoSave;

  useEffect(() => {
    autoSave.reset(toValues(member));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id, open]);

  const isDirty = !editing && form.formState.isDirty;
  useEffect(() => { onDirtyChange(isDirty); }, [isDirty, onDirtyChange]);

  const [confirmDiscard, setConfirmDiscard] = useState<null | (() => void)>(null);
  const guard = (action: () => void) => (isDirty ? setConfirmDiscard(() => action) : action());

  return (
    <>
      <Sheet open={open} modal={false} onOpenChange={(o) => { if (!o) guard(onClose); }}>
        <SheetContent side="right" hideClose onInteractOutside={(e) => e.preventDefault()} className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[480px]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex h-full min-h-0 flex-col" noValidate>
              <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <SheetDescription className="text-xs">
                    {member ? (member.has_account ? t('eventStaff.hasAccount') : t('eventStaff.invited')) : t('eventStaff.sheet.createHint')}
                  </SheetDescription>
                  <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">
                    {member ? (member.first_name || member.user_name || member.email) : t('eventStaff.newJudge')}
                  </SheetTitle>
                </div>
                {member && (
                  <>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.previous')} disabled={!onPrev} onClick={() => onPrev && guard(onPrev)}><ChevronUp size={16} /></Button>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.next')} disabled={!onNext} onClick={() => onNext && guard(onNext)}><ChevronDown size={16} /></Button>
                  </>
                )}
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={() => guard(onClose)}><X size={16} /></Button>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
                <FormField control={form.control} name="first_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('eventStaff.firstName')}</FormLabel>
                    <FormControl><Input {...field} onBlur={() => { field.onBlur(); void saveFields('first_name'); }} placeholder={t('eventStaff.firstNamePlaceholder')} autoFocus={!member} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('eventStaff.email')}</FormLabel>
                    <FormControl><Input type="email" {...field} placeholder={t('eventStaff.emailPlaceholder')} disabled={!!member} /></FormControl>
                    <FormDescription>{member ? t('eventStaff.sheet.emailLocked') : t('eventStaff.addHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="role" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('eventStaff.roleLabel')}</FormLabel>
                    <Select value={field.value} onValueChange={(v) => { if (v) { field.onChange(v); void saveFields('role'); } }}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        {STAFF_ROLES.map((r) => <SelectItem key={r} value={r}>{t(`eventStaff.role.${r}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormDescription>{t('eventStaff.sheet.roleHint')}</FormDescription>
                  </FormItem>
                )} />

                {serverError && <Alert variant="destructive"><AlertDescription>{serverError}</AlertDescription></Alert>}
              </div>

              <div className="flex items-center gap-2 border-t border-white/[0.08] px-6 py-4">
                <span className="flex flex-1 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                  {editing && <AutoSaveIndicator status={autoSave.status} retry={autoSave.retry} />}
                </span>
                {member ? (
                  <>
                    <Button type="button" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={onRemove} disabled={saving}>
                      <Trash2 size={16} />{t('eventStaff.removeAction')}
                    </Button>
                    <Button type="button" variant="ghost" onClick={onClose}>{t('common.close')}</Button>
                  </>
                ) : (
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="animate-spin" />}
                    {t('eventStaff.add')}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmDiscard !== null}
        onOpenChange={(o) => { if (!o) setConfirmDiscard(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { const next = confirmDiscard; setConfirmDiscard(null); form.reset(toValues(member)); next?.(); }}
      />
    </>
  );
}
